import React from 'react';
import { FileText, TrendingUp, TrendingDown, Receipt, Wallet, Clock, CheckCircle, BarChart3, ArrowUpRight, ShieldAlert } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { KPICard } from './ReportHelpers';

export function OverviewSection({ healthData, integrityData, setActiveReport }) {
  if (!healthData) return null;
  const h = healthData;
  const cards = [
    { title: 'Cash Position', value: formatCurrency(h.cash_position), icon: Wallet, color: h.cash_position >= 0 ? 'green' : 'red', onClick: () => setActiveReport('balance-sheet') },
    { title: 'Net Profit', value: formatCurrency(h.net_profit), icon: h.net_profit >= 0 ? TrendingUp : TrendingDown, color: h.net_profit >= 0 ? 'green' : 'red', onClick: () => setActiveReport('profit-loss') },
    { title: 'Total Expenses', value: formatCurrency(h.total_expenses), icon: Receipt, color: 'orange', onClick: () => setActiveReport('expenses') },
  ];
  if (h.vat_due !== undefined) {
    cards.push({
      title: h.vat_due >= 0 ? 'VAT Due' : 'VAT Refund',
      value: formatCurrency(Math.abs(h.vat_due)),
      icon: FileText,
      color: h.vat_due > 0 ? 'red' : 'green',
      onClick: () => setActiveReport('vat'),
    });
  }

  return (
    <div className="space-y-6" data-testid="financial-health-overview">
      {integrityData && integrityData.alerts && integrityData.alerts.length > 0 && (
        <div className="space-y-2" data-testid="integrity-alerts">
          {integrityData.alerts.map((alert, idx) => (
            <div
              key={alert.message || `alert-${idx}`}
              className={`flex items-start gap-3 p-4 rounded-lg border ${
                alert.severity === 'critical'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : alert.severity === 'warning'
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}
              data-testid={`integrity-alert-${idx}`}
            >
              <ShieldAlert className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                alert.severity === 'critical' ? 'text-red-600' : alert.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'
              }`} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-0.5">
                  {alert.severity === 'critical' ? 'Retained Earnings Alert' : alert.severity === 'warning' ? 'Data Warning' : 'Information'}
                </p>
                <p className="text-sm">{alert.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <button key={c.title} onClick={c.onClick} className="text-left transition-transform hover:scale-[1.02] active:scale-[0.98]" data-testid={`health-${c.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <KPICard title={c.title} value={c.value} icon={c.icon} color={c.color} subtitle="Click to view details" />
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-zinc-300 transition-colors" onClick={() => setActiveReport('profit-loss')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-zinc-700">Revenue vs Expenses</h3>
              <ArrowUpRight className="w-4 h-4 text-zinc-400" />
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs text-zinc-500 mb-1"><span>Revenue</span><span className="font-mono">{formatCurrency(h.total_revenue)}</span></div>
                <div className="w-full bg-zinc-100 rounded-full h-2"><div className="h-2 rounded-full bg-emerald-500" style={{ width: h.total_revenue > 0 ? '100%' : '0%' }} /></div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-zinc-500 mb-1"><span>Expenses</span><span className="font-mono">{formatCurrency(h.total_expenses)}</span></div>
                <div className="w-full bg-zinc-100 rounded-full h-2"><div className="h-2 rounded-full bg-red-400" style={{ width: h.total_revenue > 0 ? `${Math.min((h.total_expenses / h.total_revenue) * 100, 100)}%` : '0%' }} /></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-zinc-300 transition-colors" onClick={() => setActiveReport('cash-flow')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-zinc-700">Transaction Pipeline</h3>
              <ArrowUpRight className="w-4 h-4 text-zinc-400" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" /><span className="text-sm text-zinc-600">Pending</span></div>
                <span className="font-mono text-sm font-semibold">{h.pending_transactions}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-500" /><span className="text-sm text-zinc-600">Approved</span></div>
                <span className="font-mono text-sm font-semibold">{h.approved_transactions}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-sm text-zinc-600">Posted</span></div>
                <span className="font-mono text-sm font-semibold">{h.posted_transactions}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-zinc-300 transition-colors" onClick={() => setActiveReport('expenses')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-zinc-700">Quick Actions</h3>
            </div>
            <div className="space-y-2">
              <button onClick={(e) => { e.stopPropagation(); setActiveReport('profit-loss'); }} className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-zinc-50 transition-colors text-sm text-zinc-600"><TrendingUp className="w-4 h-4 text-emerald-500" />View P&L Statement</button>
              <button onClick={(e) => { e.stopPropagation(); setActiveReport('balance-sheet'); }} className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-zinc-50 transition-colors text-sm text-zinc-600"><BarChart3 className="w-4 h-4 text-blue-500" />View Balance Sheet</button>
              <button onClick={(e) => { e.stopPropagation(); setActiveReport('general-ledger'); }} className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-zinc-50 transition-colors text-sm text-zinc-600"><FileText className="w-4 h-4 text-zinc-500" />View General Ledger</button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
